<template>
    <div>
        <div v-if="loading">
            <loading></loading>
        </div>

        <div v-else>
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>

            <!-- filter -->
            <b-row v-if="!hasErrors" class="mb-3">
                <b-col md="3" class="my-1"></b-col>
                <b-col md="3" class="my-1"></b-col>

                <b-col md="4" offset-md="2" class="my-1">
                    <b-form-group class="mb-0">
                        <b-input-group size="sm">
                            <b-form-input
                                v-model="filter"
                                placeholder="Entrer la valeur recherchée"
                            />
                            <b-input-group-append>
                                <b-button
                                    :disabled="!filter"
                                    @click="resetSearch"
                                >
                                    Effacer
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
            </b-row>
            <!-- /filter -->

            <!-- Table -->
            <b-table
                responsive
                small
                :items="items"
                :fields="fields"
                :filter="filter"
                :per-page="perPage"
                :current-page="currentPage"
                @filtered="onFiltered"
            >
                <template #cell(id)="data">
                    <delete-prospect-handler
                        :id="data.item.id"
                        :can-be-deleted="true"
                        :is-lost="data.item.is_lost"
                        @status-has-changed="getItems"
                    />
                </template>
                <template #cell(prospect_is_vip)="data">
                    <span v-if="data.item.prospect_is_vip">
                      <i class="fa-fw fas fa-user-crown text-warning"/>
                    </span>
                </template>
                <template #cell(name)="data">
                    <b-link @click="goToProspect(data.item.uuid)">
                        {{ data.item.name }}
                    </b-link>
                </template>
                <template #cell(correspondent)="data">
                    <b-link @click="goToContacts(data.item.uuid)">
                        {{ data.item.correspondent }}
                    </b-link>
                </template>
                <template #cell(prospect_status_label)="data">
                    <b-badge variant="primary" style="font-size: 11px">
                        {{ data.item.prospect_status_label }}
                    </b-badge>
                </template>
                <template #cell(last_interaction_at)="data">
                    <span>
                        {{ data.item.last_interaction_at | moment('from', 'now') }}
                    </span>
                </template>
            </b-table>
            <!-- /Table -->

            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    ></b-pagination>
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import {mapState} from 'vuex'

import loading from '../../../../common/components/loading.vue'
import CommentsBlock from '../../../activity/components/CommentsBlock.vue'

import TableHandler from '../../../../common/mixins/TableHandler.vue'
import DeleteProspectHandler from './DeleteProspectHandler.vue'

export default {
    name: 'ProspectsTable',

    components: {
        DeleteProspectHandler,
        loading,
        CommentsBlock,
    },

    mixins: [TableHandler],

    props: {
        prospectType: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            fields: [
                {
                    key: 'id',
                    label: '',
                    class: 'td-sm td-icon align-middle',
                    sortable: false,
                },
                {
                    key: 'name',
                    label: 'Raison sociale',
                    class: 'td-sm td-bold align-middle',
                    sortable: true,
                },
                {
                    key: 'legal_form',
                    label: 'Type',
                    class: 'td-sm align-middle',
                    sortable: true,
                },
                {
                    key: 'correspondent',
                    label: 'Correspondant',
                    class: 'td-sm td-bold align-middle',
                    sortable: true,
                },
                {
                    key: 'last_comment',
                    label: 'Dernier commentaire',
                    class: 'td-sm align-middle',
                    sortable: true,
                },
                {
                    key: 'accounting_firm_company',
                    label: 'Cabinet',
                    class: 'td-sm align-middle',
                    sortable: true,
                },
                {
                    key: 'prospect_is_vip',
                    label: 'VIP',
                    class: 'td-sm td-icon align-middle',
                    sortable: true,
                },
                {
                    key: 'prospect_status_label',
                    label: 'Statut',
                    class: 'td-sm align-middle',
                    sortable: true,
                },
                {
                    key: 'last_interaction_at',
                    label: 'Quand',
                    class: 'td-sm text-right align-middle',
                    sortable: true,
                },
            ],
        }
    },

    computed: {
        ...mapState({
            filters: (state) => state.reportingVM.filters,
            prospectStatusFilter: (state) => state.crmMenuData.prospectStatusFilter,
        }),
    },

    async created() {
        this.filterable = false
        this.$store.commit('browsing/SET_PAGE_NAME', 'PROSPECTS')
        await this.getItems()
    },

    methods: {
        async goToProspect(uuid) {
            await this.$router.push({
                name: 'prospecting-process',
                params: {uuid},
            })
        },

        async goToContacts(uuid) {
            await this.$router.push({
                name: 'prospect-contacts',
                params: {uuid},
            })
        },

        async getItems() {
            try {
                this.loading = true
                const response = await this.$axios.get(
                    `/crm/prospects/all/${this.prospectType}`
                )
                this.items = this.prospectStatusFilter === 'ALL'
                    ? response.data.data
                    : response.data.data.filter(item => item.prospect_status === this.prospectStatusFilter)
                this.updateTotalRows()
                this.loading = false
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },
    },
}
</script>

<style scoped></style>
